import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import NotFound from '@/pages/NotFound';
import AnimatedSection from '@/components/base/AnimatedSection';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const paragraphs = article.content.split('\n\n');
  const publishDate = article.publishedAt;

  return (
    <>
      <SEOHead
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黑料吃瓜,爆料`}
        canonical={`/article/${article.slug}`}
        ogImage={article.coverImage}
        schema={[
          {
            '@type': 'WebPage',
            name: `${article.title} - ${siteInfo.name}`,
            url: `/article/${article.slug}`,
            description: article.excerpt,
            inLanguage: 'zh-CN',
            datePublished: publishDate,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `/article/${article.slug}` },
            ],
          },
          {
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: publishDate,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: siteInfo.name,
              logo: { '@type': 'ImageObject', url: '/vite.svg' },
            },
            articleSection: article.category,
            keywords: article.tags.join(','),
          },
        ]}
      />
      <Navbar />
      <main className="w-full pt-16 md:pt-20">
        <AnimatedSection>
          <section className="w-full bg-background-950 py-8 md:py-12 px-4 md:px-8 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
              <div className="flex-[3]">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="px-2 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                    {t('hotTag')}
                  </span>
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="text-sm text-primary-500 hover:underline"
                  >
                    {article.category}
                  </Link>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-serif text-foreground-50 leading-tight mb-4">
                  <span className="text-primary-500 text-3xl md:text-4xl">&ldquo;</span>
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-300 flex-wrap">
                  <span className="font-semibold text-foreground-200">{article.author}</span>
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-green-500 text-white text-[10px]">
                    <i className="ri-check-line" />
                  </span>
                  <span className="text-foreground-300">官方认证作者</span>
                </div>
              </div>
              <div className="flex-[2] w-full">
                <div className="relative w-full h-[250px] md:h-[320px] overflow-hidden rounded-lg">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - 黑料吃瓜独家爆料`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        <div className="w-full px-4 md:px-8 lg:px-12">
          <Breadcrumbs
            items={[
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <AnimatedSection delay={150}>
          <section className="w-full bg-background-100 py-8 md:py-12 px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 text-xs text-foreground-600 mb-6 pb-6 border-b border-background-200/70 flex-wrap">
                <span>{t('articlePublished')} {article.publishedAt}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-500" />
                <span>{t('articleAuthor')} {article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-500" />
                <span>{article.views.toLocaleString()} {t('articleViews')}</span>
              </div>
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-base md:text-lg text-foreground-800 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
              <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100/80 text-secondary-900 text-xs rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {relatedArticles.length > 0 && (
          <AnimatedSection delay={300}>
            <section className="w-full bg-background-50 py-8 md:py-12 px-4 md:px-8 lg:px-12">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                {t('articleRelated')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          </AnimatedSection>
        )}
      </main>
      <Footer />
    </>
  );
}